import React from 'react';
import { connect } from 'react-redux';
import * as R from 'ramda';
import { bindActionCreators, Dispatch } from 'redux';

import * as inventoryAssetActionCreators from '@atom/actions/inventoryAssetActions';
import * as inventoryCategoryActionCreators from '@atom/actions/inventoryCategoryActions';
import * as inventorySchemaActionCreators from '@atom/actions/inventorySchemaActions';
import * as poolAttributeActionCreators from '@atom/actions/poolAttributeActions';
import CategoryFilterModal from '@atom/components/common/categoryFilterModal/CategoryFilterModal';
import inventoryPendingIcon from '@atom/components/common/svgIcons/inventoryPendingIcon.svg';
import { Button, Select, SelectChangeEvent, Switch } from '@atom/mui';
import {
  getPoolAttributeColumnsSelector,
  getPoolAttributeFilterSelector,
} from '@atom/selectors/poolAttributeSelectors';
import {
  inventoryPortalAssetColumnsSelector,
  inventoryPreferencesSelector,
} from '@atom/selectors/preferencesSelectors';
import {
  getInventorySchemasSelector,
  getSchemaAttributeFilterGroupsSelector,
} from '@atom/selectors/schemaSelectors';
import colors from '@atom/styles/colors';
import {
  InventoryAssetActions,
  InventoryCategoryActions,
  InventorySchemaActions,
  PoolAttributeActions,
} from '@atom/types/actions';
import { DataType } from '@atom/types/dataType';
import { InventoryPortalView } from '@atom/types/inventory';
import { ReduxStore } from '@atom/types/store';
import { formatAssetAttributeFilters } from '@atom/utilities/assetUtilities';

import AttributeFilterGroup from './attributeFilters/AttributeFilterGroup';
import TopLevelAssetFilterToggle from './TopLevelAssetFilterToggle';

import classes from '../../styles/left-pane.module.css';

const { MenuItem } = Select;

const INITIAL_SCHEMA = {
  id: 'all',
  name: 'All',
  attributes: {},
  attributeGroups: [],
};

const initialState = {
  categoryIdsFilters: null,
  dispatch: null,
  resetKey: null,
  assetFiltersCart: {},
  topLevelAssetsOnly: true,
  hasPendingChangesOnly: false,
  schemaId: '',
};

interface PassedProps {
  updatePortalState: (data: object) => void;
  limit: number;
  rootSchema?: any;
  filters?: any[];
  topLevelAssetsOnly?: boolean;
  hasPendingChangesOnly: boolean;
  assetFilters?: any;
  categoryIdsFilters?: any[];
  view: InventoryPortalView;
  resetSelectedAssets: () => void;
}

interface ReduxStateProps {
  schemaFilters: any;
  poolAttributes: any;
  poolAttributesColumns: any;
  inventorySchemas: any[];
  preferences: any;
  inventoryFilterCategoryTree: any;
}

interface ReduxDispatchProps {
  poolAttributeActions: PoolAttributeActions;
  inventorySchemaActions: InventorySchemaActions;
  inventoryAssetActions: InventoryAssetActions;
  inventoryCategoryActions: InventoryCategoryActions;
}

type Props = PassedProps & ReduxStateProps & ReduxDispatchProps;

interface State {
  categoryIdsFilters: any;
  dispatch: any;
  resetKey: any;
  topLevelAssetsOnly: boolean;
  hasPendingChangesOnly: boolean;
  assetFilters: any;
  assetFiltersCart: any;
  filters: any[];
  schemaId: any;
}

const styles = {
  icon: {
    color: colors.neutral.gray,
    marginRight: '1rem',
  },
  toggle: {
    width: 'auto',
  },
  applyButtonStyle: {
    marginTop: '0.5rem',
  },
  resetButtonStyle: {
    marginTop: '0.5rem',
    marginLeft: '2rem',
  },
};

class InventoryAssetFilters extends React.Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      ...initialState,
      schemaId: R.pathOr(null, ['rootSchema'], props),
      assetFilters: R.pathOr(null, ['assetFilters'], props),
      assetFiltersCart: R.pathOr(null, ['assetFilters'], props),
      categoryIdsFilters: R.pathOr(null, ['categoryIdsFilters'], props),
      filters: R.pathOr(
        R.pathOr(null, ['poolAttributes'], props),
        ['filters'],
        props,
      ),
      topLevelAssetsOnly: R.pathOr(true, ['topLevelAssetsOnly'], props),
      hasPendingChangesOnly: R.pathOr(false, ['hasPendingChangesOnly'], props),
    };
  }

  componentDidMount() {
    const {
      inventorySchemaActions,
      poolAttributes,
      poolAttributeActions,
      inventorySchemas,
      updatePortalState,
    } = this.props;

    const { filters, schemaId } = this.state;

    if (R.isNil(inventorySchemas) || R.isEmpty(inventorySchemas)) {
      inventorySchemaActions.retrieveInventorySchemas({
        rootSchemas: true,
      });
    }

    const commonAttributes = poolAttributes[0];

    if (!R.isEmpty(commonAttributes.attributes)) {
      if (R.isEmpty(filters) && !schemaId) {
        this.setState({ filters: poolAttributes });
        updatePortalState({ filters: poolAttributes });
      }
    } else {
      poolAttributeActions.retrievePoolAttributes();
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { poolAttributes, updatePortalState, filters } = this.props;

    if (
      R.isEmpty(poolAttributes[0].attributes) &&
      !R.isEmpty(nextProps.poolAttributes[0].attributes)
    ) {
      this.setState({ filters: nextProps.poolAttributes });

      updatePortalState({ filters });
    }
  }

  clearSchemaFilter = async () => {
    const { updatePortalState, poolAttributes } = this.props;

    updatePortalState({
      rootSchema: INITIAL_SCHEMA,
      filters: poolAttributes,
    });

    this.setState({
      schemaId: null,
      filters: poolAttributes,
      resetKey: new Date().valueOf(),
      assetFiltersCart: {},
    });
  };

  populateAttributeFilters = async (event: SelectChangeEvent) => {
    const { value } = event.target;
    const { topLevelAssetsOnly } = this.state;
    const { updatePortalState, schemaFilters, poolAttributes } = this.props;

    if (value === 'all') {
      this.clearSchemaFilter();
      return;
    }

    const { inventorySchemas } = this.props;
    const schema = inventorySchemas.find(item => item.id === value);

    if (topLevelAssetsOnly) {
      this.setState({
        filters: getSchemaAttributeFilterGroupsSelector({ rootSchema: schema }),
      });
    }

    updatePortalState({
      rootSchema: schema,
      filters: topLevelAssetsOnly ? schemaFilters : poolAttributes,
    });
  };

  attachDispatch = dispatch => {
    this.setState({ dispatch });
  };

  mapAttributeValue = (value: any, dataType: DataType, currentValue: any) => {
    if (
      dataType === DataType.DATE ||
      dataType === DataType.NUMBER ||
      dataType === DataType.CURRENCY
    ) {
      return { ...currentValue, ...value };
    }

    return value;
  };

  onAssetFiltersChange = (
    attributeId: string,
    value: any,
    dataType: DataType,
  ) => {
    this.setState(currentState => ({
      assetFiltersCart: {
        ...currentState.assetFiltersCart,
        [attributeId]: this.mapAttributeValue(
          value,
          dataType,
          currentState.assetFiltersCart[attributeId],
        ),
      },
    }));
  };

  onTopLevelAssetFilterToggle = async () => {
    const { rootSchema, schemaFilters, poolAttributes, updatePortalState } =
      this.props;

    const topLevelAssetsOnly = !this.state.topLevelAssetsOnly;

    const filters =
      topLevelAssetsOnly && rootSchema ? schemaFilters : poolAttributes;

    updatePortalState({
      topLevelAssetsOnly,
      assetFilters: {},
      filters,
    });

    this.setState({
      topLevelAssetsOnly,
      filters,
      resetKey: new Date().valueOf(),
      assetFiltersCart: {},
    });
  };

  onHasPendingChangesToggle = () => {
    const { hasPendingChangesOnly } = this.state;
    this.setState({ hasPendingChangesOnly: !hasPendingChangesOnly });
  };

  onSubmit = () => {
    const {
      rootSchema: schema,
      inventorySchemas,
      preferences,
      inventoryAssetActions,
      updatePortalState,
      poolAttributesColumns,
      limit,
      view,
      resetSelectedAssets,
    } = this.props;

    const {
      categoryIdsFilters,
      topLevelAssetsOnly,
      filters,
      hasPendingChangesOnly,
      assetFiltersCart,
    } = this.state;

    const isListView = view === InventoryPortalView.LIST;
    const rootSchema = schema || {};

    const body = formatAssetAttributeFilters(assetFiltersCart);
    const columns =
      R.isNil(schema) || schema?.id === 'all' || !topLevelAssetsOnly
        ? poolAttributesColumns
        : inventoryPortalAssetColumnsSelector(
            rootSchema.assetType,
            preferences.assets,
            inventorySchemas,
          );

    if (isListView) {
      const data = {
        schemaId: rootSchema?.id === 'all' ? null : rootSchema.id,
        categoryIds: categoryIdsFilters,
        body,
        columns,
        rootAssets: topLevelAssetsOnly,
        hasPendingChangesOnly,
        page: 1,
        limit,
        includeInactive: false,
      };

      inventoryAssetActions.retrieveInventoryAssetsFilter(data);
    }

    updatePortalState({
      schemaId: rootSchema?.id === 'all' ? null : rootSchema.id,
      columns,
      filters,
      assetFilters: assetFiltersCart,
      poolAttributesColumns,
      topLevelAssetsOnly,
      categoryIdsFilters,
      context: 'assetsFilter',
      page: 1,
      downloadDisabled: false,
      isFilterApplied: true,
      hasPendingChangesOnly,
    });

    resetSelectedAssets();
  };

  resetFilters = async () => {
    const {
      inventoryAssetActions,
      updatePortalState,
      poolAttributesColumns,
      poolAttributes,
      limit,
    } = this.props;

    const data = {
      schemaId: null,
      rootAssets: true,
      hasPendingChangesOnly: false,
      body: {},
      columns: poolAttributesColumns,
      page: 1,
      limit,
      sortBy: 'assetName',
      isAscending: true,
      includeInactive: false,
    };

    inventoryAssetActions.retrieveInventoryAssetsFilter(data);

    updatePortalState({
      schemaId: null,
      rootSchema: INITIAL_SCHEMA,
      columns: poolAttributesColumns,
      categoryIdsFilters: null,
      assetFilters: {},
      filters: poolAttributes,
      topLevelAssetsOnly: true,
      hasPendingChangesOnly: false,
      context: 'assetsFilter',
      page: 1,
      rootAssets: true,
      sortBy: 'assetName',
      isAscending: true,
    });

    this.setState({
      schemaId: null,
      resetKey: new Date().valueOf(),
      assetFilters: {},
      assetFiltersCart: {},
      categoryIdsFilters: null,
      topLevelAssetsOnly: true,
      hasPendingChangesOnly: false,
      filters: poolAttributes,
    });
  };

  updateFiltersState = stateUpdate => {
    this.setState({
      ...stateUpdate,
    });
  };

  render() {
    const { inventorySchemas, inventoryFilterCategoryTree, rootSchema, view } =
      this.props;

    const {
      resetKey,
      assetFiltersCart,
      filters,
      categoryIdsFilters,
      topLevelAssetsOnly,
      hasPendingChangesOnly,
    } = this.state;

    return (
      <div className={classes.leftPane}>
        <div className={classes.filtersHeader}>
          <div className={classes.filtersHeaderLabel}>Filter</div>
          <Button sx={styles.resetButtonStyle} onClick={this.resetFilters}>
            Reset
          </Button>
          <Button
            color="primary"
            style={styles.applyButtonStyle}
            onClick={this.onSubmit}
          >
            Apply
          </Button>
        </div>
        {view === InventoryPortalView.LIST && (
          <div className={classes.topLevelAssetFilterToggle}>
            <TopLevelAssetFilterToggle
              toggled={topLevelAssetsOnly}
              onToggle={this.onTopLevelAssetFilterToggle}
            />
          </div>
        )}
        <div className={classes.selectFilterWrapper}>
          <Select
            fullWidth
            label="Inventory Type"
            onChange={this.populateAttributeFilters}
            value={rootSchema?.id}
          >
            {inventorySchemas.map(schema => {
              return (
                <MenuItem key={schema.id} value={schema.id}>
                  {schema.name}
                </MenuItem>
              );
            })}
          </Select>
          <div className={classes.selectFilterDescriptionText}>
            Selecting an asset type will show related filters.
          </div>
        </div>
        <div className={classes.categoryFilterContainer}>
          <CategoryFilterModal
            categories={inventoryFilterCategoryTree}
            updateFiltersState={this.updateFiltersState}
            selectedItems={categoryIdsFilters}
          />
        </div>
        <div className={classes.toggleContainer}>
          <div className={classes.toggleText}>
            <img src={inventoryPendingIcon} style={styles.icon} />
            Has Pending Changes
          </div>
          <Switch
            sx={styles.toggle}
            checked={hasPendingChangesOnly}
            onChange={this.onHasPendingChangesToggle}
          />
        </div>
        <div className={classes.attributeFiltersContainer} key={resetKey}>
          {filters.map((attributeGroup, index) => (
            <AttributeFilterGroup
              key={`${attributeGroup.name}-${index}`}
              attributeGroup={attributeGroup}
              assetFiltersCart={assetFiltersCart}
              onAssetFiltersChange={this.onAssetFiltersChange}
            />
          ))}
        </div>
      </div>
    );
  }
}

const mapStateToProps = (
  state: ReduxStore,
  ownProps: Props,
): ReduxStateProps => ({
  schemaFilters: getSchemaAttributeFilterGroupsSelector(ownProps),
  poolAttributes: getPoolAttributeFilterSelector(state),
  poolAttributesColumns: getPoolAttributeColumnsSelector(state),
  inventorySchemas: getInventorySchemasSelector(state),
  preferences: inventoryPreferencesSelector(state),
  inventoryFilterCategoryTree: state.inventoryFilterCategoryTree,
});

const mapDispatchToProps = (dispatch: Dispatch): ReduxDispatchProps => ({
  poolAttributeActions: bindActionCreators(
    poolAttributeActionCreators,
    dispatch,
  ),
  inventorySchemaActions: bindActionCreators(
    inventorySchemaActionCreators,
    dispatch,
  ),
  inventoryAssetActions: bindActionCreators(
    inventoryAssetActionCreators,
    dispatch,
  ),
  inventoryCategoryActions: bindActionCreators(
    inventoryCategoryActionCreators,
    dispatch,
  ),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(InventoryAssetFilters);
